// Report / block sheet for any other player whose handle is on screen.
//
// One component, mounted from the friends list, friend search and the
// ladder, so the three places a stranger's handle can appear all offer
// the same two exits. Both actions go to the server; nothing here is
// only cosmetic. Block also removes the player from the caller's crew and
// from future opponent picks, server-side.
import { useEffect, useState } from 'react';
import { blockPlayer, reportPlayer, type ReportReason } from '../net/friendsClient';
import { EDGES } from '../theme/tokens';

export interface PlayerRef {
  id: string;
  handle: string | null;
}

const REASONS: { id: ReportReason; label: string }[] = [
  { id: 'handle', label: 'Offensive handle' },
  { id: 'harassment', label: 'Harassment' },
  { id: 'cheating', label: 'Cheating' },
  { id: 'other', label: 'Something else' },
];

export function displayHandle(p: PlayerRef): string {
  return p.handle || `Player ${p.id.slice(0, 6)}`;
}

export function PlayerActionsSheet({
  player,
  onClose,
  onDone,
}: {
  player: PlayerRef;
  onClose: () => void;
  /** Called after a successful action so the parent can refetch. */
  onDone: (what: 'reported' | 'blocked', ok: boolean) => void;
}) {
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function submitReport() {
    if (!reason || busy) return;
    setBusy(true);
    const ok = await reportPlayer(player.id, reason, details);
    setBusy(false);
    onDone('reported', ok);
    if (ok) onClose();
  }

  async function submitBlock() {
    if (busy) return;
    setBusy(true);
    const ok = await blockPlayer(player.id);
    setBusy(false);
    onDone('blocked', ok);
    if (ok) onClose();
  }

  const name = displayHandle(player);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-asphalt/80 p-md md:items-center"
      role="dialog"
      aria-modal="true"
      aria-label={`Report or block ${name}`}
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-md flex-col gap-md rounded-md border bg-stucco1 p-md md:p-lg"
        style={{ borderColor: EDGES.soft }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-md">
          <div className="min-w-0">
            <p className="font-data text-[12px] uppercase tracking-[0.3em] text-marker">player</p>
            <p className="truncate font-cholo text-[20px] leading-tight text-bone">{name}</p>
          </div>
          <button
            onClick={onClose}
            className="min-h-[44px] px-sm font-data text-[12px] uppercase tracking-[0.2em] text-graphite hover:text-bone"
            aria-label="Close"
          >
            close
          </button>
        </div>

        <div className="flex flex-col gap-xs">
          <p className="font-data text-[12px] uppercase tracking-[0.2em] text-graphite">report for</p>
          <div className="grid grid-cols-2 gap-xs">
            {REASONS.map((r) => (
              <button
                key={r.id}
                onClick={() => setReason(r.id)}
                className={`min-h-[44px] rounded-sm border px-sm font-data text-[12px] uppercase tracking-[0.15em] transition-colors ${
                  reason === r.id
                    ? 'border-sodium bg-sodium/20 text-sodium'
                    : 'border-stucco3 text-marker hover:text-bone'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <textarea
            value={details}
            onChange={(e) => setDetails(e.target.value.slice(0, 500))}
            placeholder="anything else (optional)"
            rows={2}
            className="w-full rounded-sm border bg-asphalt px-sm py-xs font-data text-[13px] text-bone placeholder:text-graphite"
            style={{ borderColor: EDGES.soft }}
          />
          <button
            onClick={submitReport}
            disabled={!reason || busy}
            className={`min-h-[44px] rounded-sm bg-sodium px-md font-data text-[12px] uppercase tracking-[0.2em] text-sharpie hover:bg-sodiumGlow ${
              !reason || busy ? 'cursor-not-allowed opacity-50' : ''
            }`}
          >
            send report
          </button>
        </div>

        <div className="flex flex-col gap-xs border-t pt-md" style={{ borderColor: EDGES.faint }}>
          <p className="font-data text-[12px] leading-relaxed text-graphite">
            Blocking removes {name} from your crew and you will never be matched against them. They are not told.
          </p>
          <button
            onClick={submitBlock}
            disabled={busy}
            className="min-h-[44px] rounded-sm border border-aerosolRed/60 px-md font-data text-[12px] uppercase tracking-[0.2em] text-aerosolRed hover:bg-aerosolRed/10"
          >
            block player
          </button>
        </div>
      </div>
    </div>
  );
}
